import { DOCUMENT } from '@angular/common'
import { Inject, Injectable } from '@angular/core'

export interface AuthHandoffTokens {
    accessToken: string,
    refreshToken: string | null,
}

@Injectable({
    providedIn: 'root',
})
export class AuthHandoffService {
    private readonly cookieName = 'tablesense.authHandoff'
    private readonly maxAgeSeconds = 300

    constructor(@Inject(DOCUMENT) private document: Document) {}

    save(tokens: AuthHandoffTokens) {
        this.document.cookie = this.cookieString(
            encodeURIComponent(JSON.stringify(tokens)),
            this.maxAgeSeconds
        )
    }

    consume(): AuthHandoffTokens | null {
        const value = this.cookieValue()
        if (value === null) {
            return null
        }
        this.clear()
        try {
            const parsed = JSON.parse(decodeURIComponent(value)) as Partial<AuthHandoffTokens>
            if (typeof parsed.accessToken !== 'string') {
                return null
            }
            return {
                accessToken: parsed.accessToken,
                refreshToken: typeof parsed.refreshToken === 'string' ? parsed.refreshToken : null,
            }
        } catch (_) {
            return null
        }
    }

    clear() {
        this.document.cookie = this.cookieString('', 0)
    }

    private cookieValue(): string | null {
        const cookiePrefix = `${this.cookieName}=`
        const cookie = this.document.cookie
            .split(';')
            .map(part => part.trim())
            .find(part => part.startsWith(cookiePrefix))
        return cookie?.slice(cookiePrefix.length) ?? null
    }

    private cookieString(value: string, maxAgeSeconds: number): string {
        const attributes = [
            `${this.cookieName}=${value}`,
            'Path=/',
            `Max-Age=${maxAgeSeconds}`,
            'SameSite=Lax',
        ]
        const domain = this.cookieDomain()
        if (domain !== null) {
            attributes.push(`Domain=${domain}`)
        }
        if (this.document.location.protocol === 'https:') {
            attributes.push('Secure')
        }
        return attributes.join('; ')
    }

    private cookieDomain(): string | null {
        const hostname = this.document.location.hostname.toLowerCase()
        if (hostname === 'localhost' || hostname.endsWith('.localhost') || this.isIpAddress(hostname)) {
            return null
        }
        if (hostname === 'staging.tablesense.com' || hostname.endsWith('.staging.tablesense.com')) {
            return '.staging.tablesense.com'
        }
        if (hostname === 'tablesense.com' || hostname.endsWith('.tablesense.com')) {
            return '.tablesense.com'
        }
        return null
    }

    private isIpAddress(hostname: string): boolean {
        return /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname) || hostname.includes(':')
    }
}
